import React from "react";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { ColorPicker, useColor } from "react-color-palette";
import "react-color-palette/css";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { badgesStore } from "../stores/badges";
import { badgesEditModalStore } from "../stores/badgesEditModal";

function BadgeColorField({ label, value, onChange }) {
	const [showPicker, setShowPicker] = React.useState(false);
	const normalizedValue = typeof value === "string" && value.trim() ? value : "#FFFFFF";
	const [color, setColor] = useColor(normalizedValue);
	const safeColorHex = typeof color?.hex === "string" && color.hex.trim() ? color.hex : "#FFFFFF";

	return (
		<>
			{showPicker && (
				<Modal
					visible={showPicker}
					onDismiss={() => setShowPicker(false)}
					header={label}
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button variant="link" onClick={() => setShowPicker(false)}>
									Close
								</Button>
							</SpaceBetween>
						</Box>
					}>
					<ColorPicker
						color={color}
						onChange={(nextColor) => {
							setColor(nextColor);
							if (typeof nextColor?.hex === "string" && nextColor.hex.trim()) {
								onChange(nextColor.hex);
							}
						}}
					/>
				</Modal>
			)}
			<Box variant="span">{label}</Box>
			<SpaceBetween direction="horizontal" size="s">
				<div
					onClick={() => setShowPicker(true)}
					role="button"
					tabIndex={0}
					onKeyDown={(event) => {
						if (event.key === "Enter" || event.key === " ") {
							event.preventDefault();
							setShowPicker(true);
						}
					}}
					style={{
						width: "30px",
						height: "30px",
						backgroundColor: safeColorHex,
						border: "1px solid #ccc",
						cursor: "pointer",
					}}
				/>
				<Box variant="small">{safeColorHex}</Box>
			</SpaceBetween>
		</>
	);
}

class BadgesEditModal extends React.Component {
	editBadge() {
		const name = String(this.props.badgesEditModal.name || "").trim();
		if (!name.length) {
			this.props.Interface.addNoty("Error", "Badge name is required");
			return;
		}
		this.props.Interface
			.request(
				"get",
				{ class: "data", module: "badges", method: "edit" },
				{
					id: this.props.badgesEditModal.id,
					name,
					text_color: this.props.badgesEditModal.text_color,
					background_color: this.props.badgesEditModal.background_color,
					status: this.props.badgesEditModal.status.value,
				}
			)
			.then(() => {
				this.props.setBadgesEditModal({ ...this.props.badgesEditModal, show: false });
				this.props.Interface.loadBadges();
			})
			.catch((error) => {
				if (typeof error.text === "undefined") error = { text: error.toString() };
				this.props.setBadges({ ...this.props.badges, loaded: true, error });
			});
	}

	render() {
		return (
			<Modal
				visible={this.props.badgesEditModal.show}
				onDismiss={() => {
					this.props.setBadgesEditModal({ ...this.props.badgesEditModal, show: false });
				}}
				header="Edit badge"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="s">
							<Button variant="normal" onClick={() => this.props.setBadgesEditModal({ ...this.props.badgesEditModal, show: false })}>
								Close
							</Button>
							<Button variant="primary" onClick={() => this.editBadge()}>
								Ok
							</Button>
						</SpaceBetween>
					</Box>
				}>
				<SpaceBetween direction="vertical" size="m">
					<Box variant="span">Name</Box>
					<Input value={this.props.badgesEditModal.name} onChange={(input) => this.props.setBadgesEditModal({ ...this.props.badgesEditModal, name: input.detail.value })} />
					<BadgeColorField
						label="Text color"
						value={this.props.badgesEditModal.text_color}
						onChange={(text_color) => this.props.setBadgesEditModal({ ...this.props.badgesEditModal, text_color })}
					/>
					<BadgeColorField
						label="Background color"
						value={this.props.badgesEditModal.background_color}
						onChange={(background_color) => this.props.setBadgesEditModal({ ...this.props.badgesEditModal, background_color })}
					/>
					<Box variant="span">Status</Box>
					<Select selectedOption={this.props.badgesEditModal.status} onChange={(option) => this.props.setBadgesEditModal({ ...this.props.badgesEditModal, status: option.detail.selectedOption })} options={this.props.badgesEditModal.currentOption.status} />
				</SpaceBetween>
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const badges = useRecoilValue(badgesStore);
		const setBadges = useSetRecoilState(badgesStore);
		const badgesEditModal = useRecoilValue(badgesEditModalStore);
		const setBadgesEditModal = useSetRecoilState(badgesEditModalStore);
		return <Component {...props} badges={badges} setBadges={setBadges} badgesEditModal={badgesEditModal} setBadgesEditModal={setBadgesEditModal} />;
	};
}

export default withRecoil(BadgesEditModal);
